import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button.js';
/** A real modal dialog: focus moves into it on open, Escape closes it,
 * it's portalled above everything else, and the backdrop click closes it
 * — docs/architecture/craft.md accessibility rules, not just a styled
 * `<div>`. */
export function Dialog({ open, title, children, onClose, actions }) {
    const ref = useRef(null);
    useEffect(() => {
        if (!open)
            return;
        ref.current?.focus();
        const handler = (e) => {
            if (e.key === 'Escape')
                onClose();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, onClose]);
    if (!open)
        return null;
    return createPortal(_jsx("div", { className: "fixed inset-0 z-50 flex items-center justify-center bg-[#12151b]/40 p-4", onClick: onClose, children: _jsxs("div", { ref: ref, role: "dialog", "aria-modal": "true", "aria-label": title, tabIndex: -1, onClick: (e) => e.stopPropagation(), className: "w-full max-w-md overflow-hidden rounded-dialog border border-border bg-surface shadow-dialog outline-none", children: [_jsx("div", { className: "border-b border-border px-5 py-4", children: _jsx("h2", { className: "text-base font-semibold text-ink", children: title }) }), children && _jsx("div", { className: "px-5 py-4", children: children }), _jsxs("div", { className: "flex justify-end gap-2 border-t border-divider px-5 py-3.5", children: [_jsx(Button, { variant: "secondary", size: "sm", onClick: onClose, children: "Cancel" }), actions] })] }) }), document.body);
}
/** The one confirmation shape most destructive actions need — a title, a
 * sentence of consequence, and a Confirm button in whichever variant fits
 * (danger for delete, primary for a lower-stakes confirmation).
 *
 * `requireTypedConfirmation` adds a second, harder-to-misclick gate for
 * actions that are both destructive and easy to fire by accident (a bulk
 * action on a multi-row selection) — the Confirm button stays disabled
 * until the exact phrase is typed, so a reflexive click on "Delete"
 * can't go through on its own. */
export function ConfirmDialog({ open, title, description, confirmLabel = 'Confirm', variant = 'danger', onConfirm, onClose, loading, requireTypedConfirmation, }) {
    const [typed, setTyped] = useState('');
    useEffect(() => {
        if (!open)
            setTyped('');
    }, [open]);
    const locked = Boolean(requireTypedConfirmation) && typed !== requireTypedConfirmation;
    return (_jsxs(Dialog, { open: open, title: title, onClose: onClose, actions: _jsx(Button, { variant: variant, size: "sm", onClick: onConfirm, loading: loading, disabled: locked, children: confirmLabel }), children: [_jsx("p", { className: "text-sm text-text-secondary", children: description }), requireTypedConfirmation && (_jsxs("div", { className: "mt-3.5", children: [_jsxs("label", { className: "mb-1.5 block text-xs font-medium text-text-muted", children: ["Type ", _jsx("span", { className: "font-semibold text-ink", children: requireTypedConfirmation }), " to confirm"] }), _jsx("input", { type: "text", value: typed, onChange: (e) => setTyped(e.target.value), autoFocus: true, autoComplete: "off", className: "w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-ink outline-none focus:border-primary" })] }))] }));
}
//# sourceMappingURL=Dialog.js.map